import { createSlice } from "@reduxjs/toolkit";
import { genericStringSplitter } from "../../util/stringUtils";
import {
  QueryBuilders,
  ActionableQuery,
  QueryableObject,
  parseQuery,
  ValueParentQuery,
} from "../../from-server/asin-central/search";
import { getDomainFromUrl } from "../../util/urlUtils";
import { MULTISELECT_PROPERTY_KEYS } from "./searchPropertyFilter/multiSelectPropertyKeys";
const { or, and, range, filter, fuzzy, val, all } = QueryBuilders;

// Lots of queries we can legally parse out of the url are not representable with
// the search side bar UI.  Instead of throwing, we're noting that these queries
// can't be represented in the UI and executing them anyway.  This allows sophisticated
// users to make complex queries and send them to users as links.
type Unrepresentable = "unrepresentable";

const UNREPRESENTABLE_AND_BYPASS = ["asinType"];

const separator = ",";

export type Range = {
  lower: Date;
  upper: Date;
};

export type ClientQuery = ActionableQuery<QueryableObject>;

// the keys are the attribute name, the values are queries for that attribute along with the UI state
export type UIFilters = {
  [key: string]: { value: string | Range }; //query: ClientQuery;
};

// PropertyFilter and PropertyFilterGroup types allow to build nested queries using the UI
export type PropertyFilter = {
  type: "PropertyFilter";
  operator: "filter" | "fuzzy";
  value: any;
  propertyKey: string;
  /**
   * Session-scoped provenance marker. When set, identifies the token
   * as authored by a UI feature that owns the (propertyKey, value)
   * pair and needs to remove it later without touching user-authored
   * tokens that happen to share the same (propertyKey, value).
   *
   * Consumed only by the authoring feature (currently the Step
   * Breakdown pivot); read as `undefined` everywhere else.
   * `sumPropertyFilterQueries` ignores extra fields, so the marker
   * never reaches DACS or the URL query string.
   */
  internalSource?: "step-drilldown";
};
export type PropertyFilterGroup = {
  type: "PropertyFilterGroup";
  filters: Array<PropertyFilterGroup | PropertyFilter>;
  operation: "and" | "or";
};

const DEFAULT_PROPERTY_FILTER_GROUP_VALUE: PropertyFilterGroup = {
  type: "PropertyFilterGroup",
  filters: [],
  operation: "and",
};

const LOW_DATE = new Date("1000-01-01");
const HIGH_DATE = new Date("9999-01-01");

function stringArrayToQuery(
  attribute: string,
  strs: string[]
): ClientQuery | undefined {
  if (strs.length === 0) {
    return undefined;
  }
  const eachValue = strs.map((s) => filter(val(attribute, s.trim())));
  return or(eachValue[0], ...eachValue.slice(1));
}

function stringArrayToQueryWithOp(
  attribute: string,
  strs: string[],
  operator: "filter" | "fuzzy"
): ClientQuery | undefined {
  if (strs.length === 0) {
    return undefined;
  }
  const eachValue = strs.map((s) => {
    switch (operator) {
      case "filter":
        return filter(val(attribute, sanitizeInputFilters(s.trim())));
      case "fuzzy":
        return fuzzy(val(attribute, sanitizeInputFilters(s.trim())));
      default:
        return filter(val(attribute, sanitizeInputFilters(s.trim())));
    }
  });
  return or(eachValue[0], ...eachValue.slice(1));
}

export function stringToQuery(
  attribute: string,
  str?: string | string[]
): ClientQuery | undefined {
  if (typeof str !== "string" || str === "") {
    return undefined;
  }
  if (Array.isArray(str)) {
    return stringArrayToQuery(attribute, str);
  }
  if (str.indexOf(separator)) {
    return stringArrayToQuery(attribute, str.split(separator));
  }
  return filter(val(attribute, str.trim()));
}

export function stringToQueryWithOp(
  attribute: string,
  str?: string | string[],
  operator: "filter" | "fuzzy" = "filter"
): ClientQuery | undefined {
  if (typeof str !== "string" || str === "") {
    return undefined;
  }
  if (Array.isArray(str)) {
    return stringArrayToQueryWithOp(attribute, str, operator);
  }
  if (str.indexOf(separator) > -1) {
    return stringArrayToQueryWithOp(attribute, str.split(separator), operator);
  }
  switch (operator) {
    case "filter":
      return filter(val(attribute, sanitizeInputFilters(str.trim())));
    case "fuzzy":
      return fuzzy(val(attribute, sanitizeInputFilters(str.trim())));
    default:
      return filter(val(attribute, sanitizeInputFilters(str.trim())));
  }
}

export function datesToQuery(
  attribute: string,
  rangeState: Range
): ClientQuery | undefined {
  if (!rangeState.lower && !rangeState.upper) {
    return undefined;
  }
  const low = rangeState.lower || LOW_DATE;
  const high = rangeState.upper || HIGH_DATE;
  return range(val(attribute, low), val(attribute, high));
}

export const defaultQueryState: QueryState = {
  unrepresentable: false,
  queryMode: "UIFilter",
  queryPropertyFilters: DEFAULT_PROPERTY_FILTER_GROUP_VALUE,
  queryPropertyFiltersForceRehydrate: false,
  queryPropertyFiltersRehydrateSignal: 0,
  activePropertyFilters: {},
  activePropertyFiltersVisibilityEnabled: false,
  queryParameters: {},
  queryString: all().toString(),
};

/**
 * True when `group` contains at least one populated leaf filter.
 * Empty strings, `undefined`, `null`, and empty nested groups all
 * count as "no filter". Used to gate Submit and to drop the active
 * saved/predefined selection once the last chip is removed.
 */
export function hasAnyFilter(group: PropertyFilterGroup | undefined): boolean {
  if (!group || !group.filters || group.filters.length === 0) return false;
  return group.filters.some((f: PropertyFilter | PropertyFilterGroup) => {
    if (f.type === "PropertyFilter") {
      if (typeof f.value === "string") return f.value.trim().length > 0;
      return f.value !== undefined && f.value !== null;
    }
    return hasAnyFilter(f);
  });
}

/**
 * Map of asinType virtual values to the `asinParty` value they
 * imply. Both entries model UI-only keys that the backend doesn't
 * store literally — items are stored as `asinType=non_mfa` and
 * `asinParty=1p|2p`. Mirrors {@link sumQueries}, which does the
 * same expansion for the Legacy sidebar.
 */
const ASIN_TYPE_VIRTUAL_PARTY: Readonly<Record<string, string>> = {
  "1p_non_mfa": "1p",
  "2p_non_mfa": "2p",
};

/**
 * `and(filter('asinType','non_mfa'), filter('asinParty', Xp))`
 * decomposition for a virtual asinType value. Emitted alongside the
 * raw `filter('asinType', Xp_non_mfa)` marker so the backend still
 * matches items on the real stored fields, while the raw marker
 * tells the parse-back logic which UI virtual value was selected.
 */
function nonMfaDecomposition(party: string): ClientQuery {
  return and(
    filter(val("asinType", "non_mfa")),
    filter(val("asinParty", party))
  );
}

export function sumPropertyFilterQueries(
  query: PropertyFilterGroup
): ClientQuery {
  if (!query || Object.keys(query).length === 0) return all();
  const subQueries: ClientQuery[] = [];
  query?.filters.forEach(
    (customFilter: PropertyFilterGroup | PropertyFilter) => {
      if (customFilter.type === "PropertyFilterGroup") {
        subQueries.push(sumPropertyFilterQueries(customFilter));
      }
      if (customFilter.type === "PropertyFilter") {
        if (customFilter.propertyKey === "asin") {
          const value = genericStringSplitter(
            customFilter.value as string
          ).join(separator);
          const clientQuery = stringToQueryWithOp(
            customFilter.propertyKey,
            value,
            customFilter.operator
          );
          if (clientQuery) {
            subQueries.push(clientQuery);
          }
        } else if (
          customFilter.propertyKey === "asinType" &&
          typeof customFilter.value === "string" &&
          Object.prototype.hasOwnProperty.call(
            ASIN_TYPE_VIRTUAL_PARTY,
            customFilter.value
          )
        ) {
          // Emit the raw marker + party decomposition as an OR pair,
          // matching Legacy `sumQueries`. When this PF sits inside a
          // sibling OR (e.g. an asinType multiselect chip), the outer
          // OR flattens `or(or(raw, decomp), ...)` semantically on the
          // backend, so nesting is fine. `hasOwnProperty.call` avoids
          // matching prototype-chain members (`toString`, `constructor`,
          // etc.) when `customFilter.value` is one of those names.
          const party = ASIN_TYPE_VIRTUAL_PARTY[customFilter.value];
          subQueries.push(
            or(
              filter(val("asinType", customFilter.value)),
              nonMfaDecomposition(party)
            )
          );
        } else {
          const clientQuery =
            typeof customFilter.value === "string"
              ? stringToQueryWithOp(
                  customFilter.propertyKey,
                  customFilter.value,
                  customFilter.operator
                )
              : datesToQuery(customFilter.propertyKey, customFilter.value);
          if (clientQuery) {
            subQueries.push(clientQuery);
          }
        }
      }
    }
  );
  if (subQueries.length === 0) return all();
  return query.operation === "or"
    ? or(subQueries[0], ...subQueries.slice(1))
    : and(subQueries[0], ...subQueries.slice(1));
}

function sumQueries(filters: UIFilters): ClientQuery {
  const subQueries = Object.entries(filters)
    .map(([attributeName, value]) => {
      if (attributeName === "asin") {
        // Allow a poorly formatted list of ASIN's similar to SelectionCentral (see LEAP-7186).
        value.value = genericStringSplitter(value.value as string).join(
          separator
        );
      }
      if (attributeName === "asinType" && typeof value.value === "string") {
        // The UI ASIN's type filter groups types by party affiliation.
        // Most ASIN types have a one-to-one relationship with their
        // party attribute or are party-agnostic. "Non MFA (1p)" and
        // "Non MFA (2p)" share the same backend `asinType=non_mfa`
        // and differ only in `asinParty`, so the UI introduces the
        // virtual `1p_non_mfa` / `2p_non_mfa` keys and expands them
        // here to their `(asinType=non_mfa, asinParty=Xp)`
        // decomposition. `sumPropertyFilterQueries` reuses the same
        // `ASIN_TYPE_VIRTUAL_PARTY` map + `nonMfaDecomposition`
        // helper so both emit paths stay in lockstep.
        const defaultSubQueries =
          stringToQuery(attributeName, value.value)?.getSubQueries() ?? [];
        const nonMFASubQueries: ClientQuery[] = [];
        for (const [virtualKey, party] of Object.entries(
          ASIN_TYPE_VIRTUAL_PARTY
        )) {
          if (value.value.indexOf(virtualKey) !== -1) {
            nonMFASubQueries.push(nonMfaDecomposition(party));
          }
        }
        const asinTypeSubQueries = [...defaultSubQueries, ...nonMFASubQueries];
        if (asinTypeSubQueries.length === 0) return undefined;
        return or(asinTypeSubQueries[0], ...asinTypeSubQueries.slice(1));
      }
      if (value !== undefined && value.value !== undefined) {
        return typeof value?.value === "string"
          ? stringToQuery(attributeName, value.value)
          : datesToQuery(attributeName, value.value);
      }
      return undefined;
    })
    .filter((q) => q !== undefined) as ClientQuery[];
  return subQueries.length > 0
    ? and(subQueries[0], ...subQueries.slice(1))
    : all();
}

function getAttributeName(sq: ClientQuery): string {
  if (sq.type === "or" || sq.type === "and") {
    return getAttributeName(sq.getSubQueries()[0]);
  }
  return (sq as ValueParentQuery<any>).getValues()[0].attribute;
}

function getValue(sq: ClientQuery): string | Range {
  if (sq.type === "or") {
    return sq
      .getSubQueries()
      .map((q) => getValue(q))
      .filter((value) => value !== "")
      .join(", ");
  }
  if (
    sq.type === "and" &&
    UNREPRESENTABLE_AND_BYPASS.includes(getAttributeName(sq))
  )
    return "";
  if (sq.type === "range") {
    const values = (sq as ValueParentQuery<any>).getValues();
    return {
      upper: values[1].value as Date,
      lower: values[0].value as Date,
    };
  }
  return (sq as ValueParentQuery<any>).getValues()[0].value as string;
}

function spreadPropertyFilterQueries(
  query: ClientQuery,
  depth = 0
): PropertyFilterGroup | Unrepresentable {
  if (depth === 0) {
    if (query.type === "all") {
      return DEFAULT_PROPERTY_FILTER_GROUP_VALUE;
    }
    if (query.type !== "and") {
      // Property filter component can only represent queries where the
      // root is an AND.
      return "unrepresentable";
    }
    // Normalize the root shape so `rehydratePolarisPropertyFilter` —
    // which only handles asin `PropertyFilter` leaves and nested
    // `PropertyFilterGroup` children at the root — sees a tree it can
    // walk regardless of how the input was authored:
    //  - New-view links: `and(asinFilter?, and(...))` — asin (if any)
    //    extracted, remaining nested `and` recursed at depth 1.
    //  - Legacy / hand-crafted links: flat root AND with N siblings
    //    (e.g. `and(or(country...), or(programId...), range(...))`).
    //    Each non-asin sib becomes its own root child — and/or subs
    //    recurse to depth 1, bare leaves get wrapped in a single-child
    //    PFG so the rehydrate loop's PFG branch handles them (the
    //    root only recognises asin PFs otherwise).
    // Emitting each sib separately (instead of re-wrapping them under
    // a synthetic `and(...)`) keeps the tree one level shallower, so
    // legacy shapes like `or(..., and(filter, filter))` — sumQueries'
    // asinType `1p_non_mfa` decomposition — don't trip the depth cap.
    const rootSubs = query.getSubQueries();
    const asinValues: string[] = [];
    const nonAsinRootFilters: Array<PropertyFilterGroup | PropertyFilter> = [];
    for (const sub of rootSubs) {
      // Extract only exact-match `filter(asin, X)` and OR-of-`filter`
      // asin shapes into the merged asin PropertyFilter. Legacy
      // `sumQueries` never emits `fuzzy(asin, ...)`, so this covers
      // every real link. Hand-crafted `fuzzy(asin)` URLs fall through
      // to the generic leaf-wrap path below, which preserves the
      // fuzzy operator instead of silently downgrading it to `filter`
      // via the comma-joined asin merge.
      const isAsinLeaf =
        sub.type === "filter" && getAttributeName(sub) === "asin";
      const isAsinOr = sub.type === "or" && getAttributeName(sub) === "asin";
      if (isAsinLeaf || isAsinOr) {
        const v = getValue(sub);
        if (typeof v === "string" && v.trim() !== "") {
          asinValues.push(v);
        }
        continue;
      }
      if (sub.type === "and" || sub.type === "or") {
        const nested = spreadPropertyFilterQueries(sub, 1);
        if (nested === "unrepresentable") {
          return "unrepresentable";
        }
        if (nested.filters.length > 0) {
          nonAsinRootFilters.push(nested);
        }
      } else {
        // Bare leaf on a non-asin property (typically a `range` on
        // announceDate). Wrap in a single-child PFG so the rehydrate
        // loop dispatches it through the PFG branch. The wrapping
        // `and` operation is a no-op on a single filter.
        const attribute = getAttributeName(sub);
        const value = getValue(sub);
        nonAsinRootFilters.push({
          type: "PropertyFilterGroup",
          operation: "and",
          filters: [
            {
              type: "PropertyFilter",
              propertyKey: attribute,
              value: value ?? "",
              operator: sub.type === "fuzzy" ? "fuzzy" : "filter",
            },
          ],
        });
      }
    }

    const rootFilters: Array<PropertyFilterGroup | PropertyFilter> = [];
    if (asinValues.length > 0) {
      // Join into a single asin PropertyFilter. `getValue` on an
      // `or(filter(asin,X), filter(asin,Y))` already returns "X, Y",
      // and `rehydratePolarisPropertyFilter` splits on commas, so
      // multiple asin sources merge without extra parsing.
      rootFilters.push({
        type: "PropertyFilter",
        propertyKey: "asin",
        value: asinValues.join(", "),
        operator: "filter",
      });
    }
    rootFilters.push(...nonAsinRootFilters);
    return {
      type: "PropertyFilterGroup",
      filters: rootFilters,
      operation: "and",
    };
  }

  if (depth > 2) {
    // Property filter supports at most root level + two nested levels.
    return "unrepresentable";
  }

  const subQueries: Array<PropertyFilterGroup | PropertyFilter> = [];
  for (const subQuery of query.getSubQueries()) {
    if (subQuery.type === "and" || subQuery.type === "or") {
      const nested = spreadPropertyFilterQueries(subQuery, depth + 1);
      if (nested === "unrepresentable") {
        return "unrepresentable";
      }
      if (nested?.filters?.length > 0) {
        subQueries.push(nested);
      }
    } else {
      const attribute = getAttributeName(subQuery);
      const value = getValue(subQuery);
      // Only `filter` and `fuzzy` map to a PropertyFilter operator;
      // `range` leaves carry a `{lower, upper}` value that the emit
      // side routes on value type, so operator="filter" is correct.
      subQueries.push({
        propertyKey: attribute,
        value: value ?? "",
        operator: subQuery.type === "fuzzy" ? "fuzzy" : "filter",
        type: "PropertyFilter",
      });
    }
  }
  return {
    type: "PropertyFilterGroup",
    filters: subQueries,
    operation: query?.type === "or" ? "or" : "and",
  };
}

function spreadQueries(query: ClientQuery): UIFilters | Unrepresentable {
  if (query.type === "all") {
    return {};
  }
  // the search side bar component can only represent queries where the root is an AND
  if (query.type !== "and") {
    return "unrepresentable";
  }
  const subQueries = query.getSubQueries();
  const uiLegalSecondGenNodes = new Set(["or", "range", "filter", "fuzzy"]);
  if (
    subQueries.filter((sq) => !uiLegalSecondGenNodes.has(sq.type)).length > 0
  ) {
    return "unrepresentable";
  }
  if (subQueries.length < 1) {
    return "unrepresentable";
  }
  return subQueries.reduce((uiFilters: UIFilters, sq) => {
    uiFilters[getAttributeName(sq)] = {
      value: getValue(sq),
    };
    return uiFilters;
  }, {});
}

function parsePropertyFilterQueryClientSide(
  query: string
): PropertyFilterGroup | Unrepresentable {
  // Two normalisation passes before spreading:
  //  1. Drop the Legacy `sumQueries` non-MFA decomposition inside
  //     any OR whose sibling raw marker (`filter('asinType',
  //     Xp_non_mfa)`) already conveys the same UI selection. This
  //     lets Polaris render one multi-select chip instead of a
  //     compound OR with a nested (asinType, asinParty) AND that
  //     Cloudscape can't render past one level of grouping.
  //     `sumPropertyFilterQueries` re-emits the same decomposition
  //     on the way out, so the backend match round-trips.
  //  2. Flatten `or(or(...), ...)` chains whose leaves all target a
  //     single multi-select enum property (e.g. `asinType`). Nested
  //     OR-of-OR arrangements can come from Polaris' token-group UI
  //     when a user drops two enum tokens for the same property
  //     into a nested group, and would otherwise trip the depth cap
  //     in `spreadPropertyFilterQueries`.
  return spreadPropertyFilterQueries(
    flattenEnumOrsInClientQuery(
      stripNonMfaDecompositionClauses(parseQuery(query))
    )
  );
}

/**
 * Recursively strip `and(filter('asinType','non_mfa'), filter(
 * 'asinParty', Xp))` clauses from any OR they appear in. Legacy
 * `sumQueries` emits these alongside a raw `filter('asinType',
 * Xp_non_mfa)` marker for each selected virtual value; on the parse
 * side the raw marker is enough to reconstruct the UI selection, so
 * the AND decomposition is redundant metadata that only blocks
 * Polaris from collapsing the OR into a single multi-select chip.
 * `sumPropertyFilterQueries` regenerates the same clauses on emit,
 * so the URL and backend match round-trip.
 */
function stripNonMfaDecompositionClauses(query: ClientQuery): ClientQuery {
  /**
   * Returns the `1p` / `2p` party if `q` matches the shape
   * `and(filter('asinType','non_mfa'), filter('asinParty', Xp))`
   * in either sub-query order, else `null`.
   */
  const matchNonMfaAndClause = (q: ClientQuery): "1p" | "2p" | null => {
    if (q.type !== "and") return null;
    const subs = q.getSubQueries();
    if (subs.length !== 2) return null;
    const [a, b] = subs;
    if (a.type !== "filter" || b.type !== "filter") return null;
    const attrA = getAttributeName(a);
    const attrB = getAttributeName(b);
    const valA = getValue(a);
    const valB = getValue(b);
    const partyFromPair = (
      typeAttr: string,
      typeVal: string | Range,
      partyAttr: string,
      partyVal: string | Range
    ): "1p" | "2p" | null => {
      if (typeAttr !== "asinType" || typeVal !== "non_mfa") return null;
      if (partyAttr !== "asinParty") return null;
      if (partyVal === "1p" || partyVal === "2p") return partyVal;
      return null;
    };
    return (
      partyFromPair(attrA, valA, attrB, valB) ??
      partyFromPair(attrB, valB, attrA, valA)
    );
  };

  /**
   * True when `subs` contains a raw `filter('asinType', Xp_non_mfa)`
   * marker for the given party — the sibling that
   * `sumPropertyFilterQueries` re-expands into the decomposition on
   * emit. Only in that case is the AND clause redundant metadata
   * and safe to drop.
   */
  const hasVirtualMarker = (subs: ClientQuery[], party: "1p" | "2p") => {
    const markerValue = party === "1p" ? "1p_non_mfa" : "2p_non_mfa";
    return subs.some(
      (s) =>
        s.type === "filter" &&
        getAttributeName(s) === "asinType" &&
        getValue(s) === markerValue
    );
  };

  const walk = (q: ClientQuery): ClientQuery => {
    if (q.type === "or") {
      const rawSubs = q.getSubQueries();
      const kept = rawSubs
        .filter((sub) => {
          const party = matchNonMfaAndClause(sub);
          if (party === null) return true;
          // Only strip the AND when the paired raw marker sits next
          // to it. Without the marker, the emit path has no way to
          // regenerate the decomposition, so dropping it would
          // silently lose the actual backend matcher.
          return !hasVirtualMarker(rawSubs, party);
        })
        .map(walk);
      if (kept.length === 0) return q; // Defensive: preserve original.
      if (kept.length === 1) return kept[0];
      return or(kept[0], ...kept.slice(1));
    }
    if (q.type === "and") {
      const subs = q.getSubQueries().map(walk);
      if (subs.length === 0) return q;
      return and(subs[0], ...subs.slice(1));
    }
    return q;
  };

  return walk(query);
}

/**
 * Mirror of {@link flattenEnumOrsInClientQuery} for the internal
 * `PropertyFilter`/`PropertyFilterGroup` tree. Recursively flattens
 * `PropertyFilterGroup{or, ...}` chains whose leaves all target a
 * single `MULTISELECT_PROPERTY_KEYS` property into one flat
 * `PropertyFilterGroup{or, filters: [PF, PF, ...]}`. Called on the
 * emit side (`PropertyFilterInput`) before dispatch so we never
 * store — or serialise into the URL — an OR-of-OR arrangement that
 * would otherwise trip the depth-2 limit on the parse side.
 */
export function flattenEnumOrGroupsInternal(
  group: PropertyFilterGroup
): PropertyFilterGroup {
  // Recurse first, and unwrap any single-leaf OR returned by a nested
  // call into a bare `PropertyFilter`. Mirrors the ClientQuery-side
  // flatten, which returns the bare leaf when only one survives dedup
  // — this one can only unwrap during recursion (not at the return),
  // since the function is typed to return a `PropertyFilterGroup`.
  const children: Array<PropertyFilter | PropertyFilterGroup> = [];
  for (const child of group.filters ?? []) {
    if (child.type !== "PropertyFilterGroup") {
      children.push(child);
      continue;
    }
    const flattened = flattenEnumOrGroupsInternal(child);
    if (
      flattened.operation === "or" &&
      flattened.filters.length === 1 &&
      flattened.filters[0].type === "PropertyFilter"
    ) {
      children.push(flattened.filters[0]);
    } else {
      children.push(flattened);
    }
  }
  if (group.operation === "or") {
    const leaves = collectSameEnumInternalLeaves(children);
    if (leaves && leaves.length > 0) {
      return {
        type: "PropertyFilterGroup",
        operation: "or",
        filters: dedupePropertyFiltersByValue(leaves),
      };
    }
  }
  return { ...group, filters: children };
}

/**
 * Dedupe `PropertyFilter`s by value so a repeated `(propertyKey,
 * value)` pair (e.g. from a hand-crafted `or(filter(k,v),
 * filter(k,v))` URL) doesn't survive flattening into the flat OR
 * group — otherwise the emit side's `maybeCollapseToEnumToken`
 * would build an enum token with a duplicated value. Callers
 * guarantee all filters share one `propertyKey`, so keying by
 * value alone is sufficient.
 */
function dedupePropertyFiltersByValue(
  filters: PropertyFilter[]
): PropertyFilter[] {
  const seen = new Set<string>();
  const out: PropertyFilter[] = [];
  for (const f of filters) {
    const vk = typeof f.value === "string" ? f.value : JSON.stringify(f.value);
    if (seen.has(vk)) continue;
    seen.add(vk);
    out.push(f);
  }
  return out;
}

function collectSameEnumInternalLeaves(
  nodes: Array<PropertyFilter | PropertyFilterGroup>
): PropertyFilter[] | null {
  const collected: PropertyFilter[] = [];
  let key: string | null = null;
  for (const node of nodes) {
    if (node.type === "PropertyFilter") {
      if (!MULTISELECT_PROPERTY_KEYS.has(node.propertyKey)) return null;
      if (node.operator !== "filter") return null;
      if (key === null) key = node.propertyKey;
      else if (node.propertyKey !== key) return null;
      collected.push(node);
    } else if (node.type === "PropertyFilterGroup" && node.operation === "or") {
      const sub = collectSameEnumInternalLeaves(node.filters ?? []);
      if (sub === null) return null;
      for (const leaf of sub) {
        if (key === null) key = leaf.propertyKey;
        else if (leaf.propertyKey !== key) return null;
        collected.push(leaf);
      }
    } else {
      return null;
    }
  }
  return collected;
}

/**
 * Recursively rewrite `or(...)` nodes whose transitive leaves are
 * all `filter(attr,v)` clauses on a single `MULTISELECT_PROPERTY_KEYS`
 * attribute into one flat `or(filter, filter, ...)`. Mixed-property
 * ORs, non-enum ORs, and any tree that reaches a non-`or`/non-`filter`
 * node are left as-is; we still recurse into their children so
 * matching chains buried inside AND branches also get flattened.
 */
function flattenEnumOrsInClientQuery(query: ClientQuery): ClientQuery {
  if (query.type === "or") {
    const flat = collectSameEnumFilterLeaves(query);
    if (flat && flat.length > 0) {
      // Dedupe by value so `filter(k,v) OR filter(k,v)` collapses to
      // a single leaf — mirror of the internal-side dedup above.
      const seen = new Set<string>();
      const deduped: ClientQuery[] = [];
      for (const leaf of flat) {
        const v = getValue(leaf);
        const vk = typeof v === "string" ? v : JSON.stringify(v);
        if (seen.has(vk)) continue;
        seen.add(vk);
        deduped.push(leaf);
      }
      return deduped.length === 1
        ? deduped[0]
        : or(deduped[0], ...deduped.slice(1));
    }
  }
  if (query.type === "and" || query.type === "or") {
    const subs = query
      .getSubQueries()
      .map((s: ClientQuery) => flattenEnumOrsInClientQuery(s));
    return query.type === "and"
      ? and(subs[0], ...subs.slice(1))
      : or(subs[0], ...subs.slice(1));
  }
  return query;
}

/**
 * Returns the flat list of leaf `filter` nodes if `query` is either
 * a `filter` on a multi-select enum property, or an `or` whose
 * subtree contains only such leaves (all on the same property).
 * Returns `null` when any branch violates that shape so the caller
 * preserves the original structure.
 */
function collectSameEnumFilterLeaves(query: ClientQuery): ClientQuery[] | null {
  if (query.type === "filter") {
    const attr = getAttributeName(query);
    if (!MULTISELECT_PROPERTY_KEYS.has(attr)) return null;
    return [query];
  }
  if (query.type === "or") {
    const collected: ClientQuery[] = [];
    let attr: string | null = null;
    for (const sub of query.getSubQueries()) {
      const subLeaves = collectSameEnumFilterLeaves(sub);
      if (subLeaves === null) return null;
      for (const leaf of subLeaves) {
        const leafAttr = getAttributeName(leaf);
        if (attr === null) attr = leafAttr;
        else if (leafAttr !== attr) return null;
        collected.push(leaf);
      }
    }
    return collected;
  }
  return null;
}

/**
 * Parse an applied queryString back to its PropertyFilterGroup form.
 * Returns `undefined` for queries that can't be round-tripped
 * through the property-filter tree (legacy UIFilter-only queries or
 * clauses the property-filter parser deliberately drops), and also
 * for queries that throw during parse. Callers are expected to fall
 * back to the raw queryString in that case. Safe to call inside a
 * render / useMemo since it never throws.
 */
export function parsePropertyFilterFromQueryString(
  query: string
): PropertyFilterGroup | undefined {
  try {
    const parsed = parsePropertyFilterQueryClientSide(query);
    return parsed === "unrepresentable" ? undefined : parsed;
  } catch {
    return undefined;
  }
}

function parseQueryClientSide(query: string): UIFilters | Unrepresentable {
  return spreadQueries(parseQuery(query));
}

/**
 * `queryPropertyFiltersRehydrateSignal` is owned by the reducers,
 * not by URL parsing — the URL doesn't carry a rehydrate epoch. Any
 * caller that assigns the parsed state onto the slice is responsible
 * for bumping the signal separately if it wants PropertyFilterInput
 * to rehydrate.
 */
type ParsedQueryState = Omit<QueryState, "queryPropertyFiltersRehydrateSignal">;

function getStateFromString(
  query: string,
  queryMode: "UIFilter" | "PropertyFilter" = "UIFilter"
): ParsedQueryState {
  try {
    // Default UI Filters
    if (queryMode === "UIFilter") {
      const parsedUIFilters = parseQueryClientSide(query);
      if (parsedUIFilters !== "unrepresentable") {
        // Also seed `queryPropertyFilters` from the PropertyFilter
        // parse so App Shell's chip UI rehydrates on legacy links
        // (which the Legacy view still writes as `queryMode=UIFilter`).
        // The Legacy view keeps reading `queryParameters`, so its
        // behavior is unchanged.
        //
        // Wrapped in its own try/catch so a parse failure here can
        // never demote a successful UIFilter parse into the
        // unrepresentable branch — chips just stay empty and the
        // query still executes via `queryString`.
        let seededPropertyFilters: PropertyFilterGroup =
          DEFAULT_PROPERTY_FILTER_GROUP_VALUE;
        let seededChips = false;
        // Default `queryString` to the legacy `sumQueries` shape.
        // When the PropertyFilter parse + emit both succeed we
        // overwrite it with the property-filter shape so FilterBar's
        // isDirty check (pending vs. applied) stays clean on load.
        // Both shapes are semantically equivalent — `and(a, b, c)`
        // and `and(and(a, b, c))` compute the same set on the
        // backend.
        let queryString = sumQueries(parsedUIFilters).toString();
        try {
          const parsedPropertyFilters =
            parsePropertyFilterQueryClientSide(query);
          if (parsedPropertyFilters !== "unrepresentable") {
            // Emit inside the try so a throw during serialisation
            // also falls back to empty chips + legacy queryString
            // instead of propagating out to the outer try/catch
            // (which would flip the state to unrepresentable and
            // wipe the UIFilter parse).
            queryString = sumPropertyFilterQueries(
              parsedPropertyFilters
            ).toString();
            seededPropertyFilters = parsedPropertyFilters;
            seededChips = true;
          }
        } catch {
          // Fall through with chips empty and the legacy queryString.
        }
        return {
          unrepresentable: false,
          queryMode: queryMode,
          queryPropertyFilters: seededPropertyFilters,
          queryPropertyFiltersForceRehydrate: seededChips,
          activePropertyFilters: {},
          activePropertyFiltersVisibilityEnabled: false,
          queryParameters: parsedUIFilters,
          queryString,
          queryParseError: undefined,
        };
      }
    }
    // Property Filters
    if (queryMode === "PropertyFilter") {
      const parsedPropertyFilters = parsePropertyFilterQueryClientSide(query);
      if (parsedPropertyFilters !== "unrepresentable") {
        return {
          unrepresentable: false,
          queryMode: queryMode,
          queryParameters: {},
          queryPropertyFilters: parsedPropertyFilters,
          queryPropertyFiltersForceRehydrate: true,
          activePropertyFilters: {},
          activePropertyFiltersVisibilityEnabled: false,
          queryString: sumPropertyFilterQueries(
            parsedPropertyFilters
          ).toString(),
          queryParseError: undefined,
        };
      }
    }
    // Unrepresentable Filters
    return {
      unrepresentable: true,
      queryMode: queryMode,
      queryPropertyFilters: DEFAULT_PROPERTY_FILTER_GROUP_VALUE,
      queryPropertyFiltersForceRehydrate: false,
      activePropertyFilters: {},
      activePropertyFiltersVisibilityEnabled: false,
      queryParameters: {},
      queryString: query,
      queryParseError: undefined,
    };
  } catch (e: any) {
    return {
      unrepresentable: true,
      queryMode: queryMode,
      queryPropertyFilters: DEFAULT_PROPERTY_FILTER_GROUP_VALUE,
      queryPropertyFiltersForceRehydrate: false,
      activePropertyFilters: {},
      activePropertyFiltersVisibilityEnabled: false,
      queryParameters: {},
      queryString: query,
      queryParseError: (e as Error).toString(),
    };
  }
}

function getStartingQuery(): QueryState {
  if (typeof window === "object") {
    const queryFromUrl = window.location.href.split("#")[1];
    if (queryFromUrl !== undefined && queryFromUrl.trim() !== "") {
      const queryUri = decodeURI(queryFromUrl.split("(queryMode)")[0]);
      const queryMode = queryFromUrl.includes("(queryMode)=PropertyFilter")
        ? "PropertyFilter"
        : "UIFilter";
      const parsed = getStateFromString(queryUri, queryMode);
      // App-mount PropertyFilterInput will read the initial state and
      // rehydrate on its own [] effect; seed the signal to 1 so any
      // subsequent same-value bump is still a distinct edge.
      return {
        ...parsed,
        queryPropertyFiltersRehydrateSignal:
          parsed.queryPropertyFiltersForceRehydrate ? 1 : 0,
      };
    }
  }
  return defaultQueryState;
}

function writeQueryToUrl(
  query: string,
  queryMode: "UIFilter" | "PropertyFilter" = "UIFilter"
) {
  //budget routing
  if (typeof window === "object") {
    const domain = getDomainFromUrl();
    window.location.replace(`${domain}#${query}(queryMode)=${queryMode}`);
  }
}

/** Strip the URL hash silently (no popstate, no page reload). */
function clearQueryFromUrl() {
  if (typeof window === "object" && window.location.hash) {
    window.history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search
    );
  }
}

// Sanitize input text to match with expected search.ts valueTupleMatcher
// This helps to reduce 500 errors when characters are not allowed by valueTupleMatcher
const sanitizeInputFilters = (value: string): string => {
  if (!value || value === "") return "";
  // Replace all characters that don't match the allowed pattern
  return value.replace(/[^\w\s\-.,:+\/|]/g, "-");
};

type QueryState = {
  unrepresentable: boolean;
  queryMode: "PropertyFilter" | "UIFilter";
  queryPropertyFilters: PropertyFilterGroup;
  queryPropertyFiltersForceRehydrate: boolean;
  /**
   * Monotonic counter that increments every time an external writer
   * changes queryPropertyFilters and wants PropertyFilterInput to
   * push the new tree into its Polaris local state. Unlike the
   * boolean forceRehydrate flag (which the consumer resets and
   * therefore races with concurrent producers under React 17's
   * unbatched-effect semantics), the counter only ever increases —
   * so any producer, at any time, can trigger a rehydrate without
   * coordinating with the consumer.
   */
  queryPropertyFiltersRehydrateSignal: number;
  activePropertyFilters: { [key: string]: boolean };
  activePropertyFiltersVisibilityEnabled: boolean;
  queryParameters: UIFilters;
  queryString: string;
  pendingQueryString?: string;
  queryParseError?: string;
};

const generateQuery = createSlice({
  name: "queryParameters",
  initialState: getStartingQuery(),
  reducers: {
    resetFilter(state) {
      state.unrepresentable = false;
      state.queryParameters = {};
      state.queryPropertyFilters = DEFAULT_PROPERTY_FILTER_GROUP_VALUE;
      state.queryPropertyFiltersForceRehydrate = true;
      state.queryPropertyFiltersRehydrateSignal =
        (state.queryPropertyFiltersRehydrateSignal ?? 0) + 1;
      state.activePropertyFilters = {};
      state.activePropertyFiltersVisibilityEnabled = false;
      state.queryString = "(all)";
      state.pendingQueryString = undefined;
      state.queryParseError = undefined;
    },
    setQueryMode(state, action) {
      state.queryMode = action.payload;
    },
    loadFromUrl(state) {
      const query = getStartingQuery();
      const hasHash =
        typeof window === "object" &&
        (window.location.href.split("#")[1] ?? "").trim() !== "";
      state.unrepresentable = query.unrepresentable;
      state.queryMode = query.queryMode;
      state.queryPropertyFilters = query.queryPropertyFilters;
      state.queryPropertyFiltersForceRehydrate =
        query.queryPropertyFiltersForceRehydrate;
      if (query.queryPropertyFiltersForceRehydrate) {
        state.queryPropertyFiltersRehydrateSignal =
          (state.queryPropertyFiltersRehydrateSignal ?? 0) + 1;
      }
      state.activePropertyFilters = query.activePropertyFilters;
      state.activePropertyFiltersVisibilityEnabled =
        query.activePropertyFiltersVisibilityEnabled;
      state.queryParameters = query.queryParameters;
      state.queryParseError = query.queryParseError;
      // Only overwrite queryString when the URL has a hash — hash-less
      // parse yields "(all)" and would clobber a cleared query.
      if (hasHash) {
        state.queryString = query.queryString;
      }
    },
    /**
     * Wipe queryString so table fetch guards skip, and strip the URL
     * hash so a subsequent nav doesn't repopulate stale filters.
     * App Shell only.
     */
    clearAppliedQuery(state) {
      state.queryString = "";
      state.pendingQueryString = undefined;
      clearQueryFromUrl();
    },
    setAttributeQuery(state, action) {
      if (action.payload.queryState !== undefined) {
        state.queryParameters = {
          ...(state.unrepresentable ? {} : state.queryParameters),
          [action.payload.attribute]: action.payload.queryState,
        };
        state.unrepresentable = false;
      }
    },
    setQueryPropertyFilters(state, action) {
      state.queryPropertyFilters = action.payload;
      state.unrepresentable = false;
    },
    setQueryPropertyFiltersForceRehydrate(state, action) {
      state.queryPropertyFiltersForceRehydrate = action.payload;
      // Bump the monotonic rehydrate signal every time a caller
      // requests a rehydrate. PropertyFilterInput observes the
      // signal (not the boolean) so races between concurrent
      // producers and PropertyFilterInput's consume-and-reset
      // effect can't drop a rehydrate. Under React 17's unbatched
      // useEffect updates, the boolean can flip true → false → true
      // across separate renders in the same commit; the counter
      // records every "true" request as a distinct edge.
      if (action.payload === true) {
        state.queryPropertyFiltersRehydrateSignal =
          (state.queryPropertyFiltersRehydrateSignal ?? 0) + 1;
      }
    },
    resetActivePropertyFilters(state) {
      state.activePropertyFilters = {};
    },
    setActivePropertyFilter(state, action) {
      state.activePropertyFilters = {
        ...state.activePropertyFilters,
        [action.payload]: true,
      };
    },
    setActivePropertyFiltersVisibilityEnabled(state, action) {
      state.activePropertyFiltersVisibilityEnabled = action.payload;
    },
    unsetAttributeQuery(state, action) {
      if (action.payload.attribute !== undefined) {
        delete state.queryParameters[action.payload.attribute];
      }
    },
    setQueryString(state) {
      if (
        state.pendingQueryString !== undefined &&
        state.pendingQueryString !== ""
      ) {
        const newState = getStateFromString(
          state.pendingQueryString,
          state.queryMode
        );
        writeQueryToUrl(newState.queryString, state.queryMode);
        // Preserve the monotonic signal across Object.assign, then
        // bump if the parsed state requested a rehydrate.
        const preservedSignal = state.queryPropertyFiltersRehydrateSignal;
        Object.assign(state, newState);
        state.queryPropertyFiltersRehydrateSignal =
          preservedSignal +
          (newState.queryPropertyFiltersForceRehydrate ? 1 : 0);
      } else if (!state.unrepresentable) {
        if (state.queryMode === "UIFilter") {
          const queryString = sumQueries(state.queryParameters).toString();
          writeQueryToUrl(queryString);
          state.queryString = queryString;
        }
        if (state.queryMode === "PropertyFilter") {
          const queryString = sumPropertyFilterQueries(
            state.queryPropertyFilters
          ).toString();
          writeQueryToUrl(queryString, "PropertyFilter");
          state.queryString = queryString;
        }
      }
    },
    setPendingQueryString(state, action) {
      state.pendingQueryString = action.payload;
    },
  },
});

export const {
  setQueryMode,
  setQueryPropertyFilters,
  setQueryPropertyFiltersForceRehydrate,
  setActivePropertyFilter,
  setActivePropertyFiltersVisibilityEnabled,
  resetActivePropertyFilters,
  resetFilter,
  setAttributeQuery,
  unsetAttributeQuery,
  setQueryString,
  setPendingQueryString,
  loadFromUrl,
  clearAppliedQuery,
} = generateQuery.actions;

export default generateQuery.reducer;
